/* Alma Comfort theme — all storefront behaviour, no dependencies.
   Behaviour mirrors the Claude Design "Tank PDP v3" (design/tank-pdp-v3.dc.html).
   Data from Liquid arrives via #alma-config (snippets/theme-config.liquid). */
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const list = s => String(s || '').split(',').map(x => x.trim()).filter(Boolean);

  const CFG = (() => { try { return JSON.parse($('#alma-config').textContent); } catch (e) { return {}; } })();
  const P = CFG.product;

  /* ───── Money ───── */
  // Shopper's currency (Shopify Markets): cart/product amounts arrive already converted.
  const CUR = (window.Shopify && Shopify.currency) || {};
  const ACTIVE = CUR.active || CFG.shopCurrency;
  const RATE = parseFloat(CUR.rate) || 1;
  function money(cents) {
    // The shop's own currency keeps its money format (€39,95); others get the standard local format (454,00 kr).
    if (ACTIVE && CFG.shopCurrency && ACTIVE !== CFG.shopCurrency) {
      // Whole amounts drop the empty decimals, like Liquid's money_without_trailing_zeros (908 kr, but 56,16 kr).
      const whole = cents % 100 === 0;
      try { return new Intl.NumberFormat(CFG.locale || 'en', { style: 'currency', currency: ACTIVE, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }).format(cents / 100); } catch (e) {}
    }
    const fmt = CFG.moneyFormat || '€{{amount}}';
    // Like money_without_trailing_zeros in Liquid: whole amounts drop the decimals (59 kr, 1 347 kr; but 39,95 €).
    const group = (n, dec, th, ds) => {
      if (n % 100 === 0) dec = 0;
      const [i, f] = (n / 100).toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (f ? ds + f : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: group(cents, 2, ',', '.'),
      amount_no_decimals: group(cents, 0, ',', '.'),
      amount_with_comma_separator: group(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: group(cents, 0, '.', ','),
      amount_with_apostrophe_separator: group(cents, 2, "'", '.'),
      amount_with_space_separator: group(cents, 2, ' ', ',')
    }[k] || group(cents, 2, ',', '.')));
  }

  /* ───── Product model ───── */
  const SWATCH = {};
  String(CFG.swatches || '').split(/\n/).forEach(l => { const [n, h] = l.split(':'); if (n && h) SWATCH[n.trim().toLowerCase()] = h.trim(); });
  const hexOf = name => SWATCH[String(name || '').toLowerCase()] || '#E3E6E4';
  const optIdx = re => (P ? P.options.findIndex(o => re.test(o)) : -1);
  const CI = optIdx(/colo|kleur|färg/i);
  const SI = optIdx(/size|maat|storlek/i);
  const uniq = i => (P && i >= 0 ? [...new Set(P.variants.map(v => v.options[i]))] : []);
  const COLOURS = uniq(CI);
  const SIZES = uniq(SI);
  const UNIT = P ? (P.variants.find(v => v.available) || P.variants[0] || {}).price || 0 : 0;
  const CART_COLOURS = list(CFG.cartColours).filter(c => !COLOURS.length || COLOURS.includes(c));
  const TIERS = list(CFG.tiers).map(t => { const [size, free] = t.split(':').map(Number); return { size, free }; })
    .filter(t => t.size > 0 && t.free >= 0).sort((a, b) => b.size - a.size);
  const MIN_BUNDLE = TIERS.length ? TIERS[TIERS.length - 1].size : Infinity;
  // Single-tank shipping fee: the "shipping_fees" setting has one line per currency (SEK:59) so it can match
  // the rate set in that currency in admin. A currency that isn't listed gets the first line converted.
  const SHIP_FEE = (() => {
    const fees = String(CFG.shippingFees || '').split(/\n/).map(l => l.split(':').map(x => x.trim())).filter(([c, a]) => c && a)
      .map(([c, a]) => [c.toUpperCase(), Math.round(parseFloat(a.replace(',', '.')) * 100)]);
    const own = fees.find(([c]) => c === ACTIVE);
    if (own) return own[1];
    const base = fees.find(([c]) => c === CFG.shopCurrency) || fees[0];
    return base ? Math.round(base[1] * (base[0] === CFG.shopCurrency ? RATE : 1)) : 0;
  })();
  // "Each" prices: whole-unit currencies (tank = 454 kr) round to whole units (303 kr each); EUR keeps cents (€26,63).
  const perTank = cents => (UNIT % 100 === 0 ? Math.round(cents / 100) * 100 : Math.round(cents));

  function variantFor(colour, size) {
    if (!P) return null;
    return P.variants.find(v => (CI < 0 || v.options[CI] === colour) && (SI < 0 || v.options[SI] === size)) || null;
  }
  function imgFor(colour) {
    const v = P && CI >= 0 && P.variants.find(x => x.options[CI] === colour && x.image);
    return (v && v.image) || (CFG.assets && CFG.assets.fallback[colour]) || null;
  }
  // Bundle maths, same greedy split as the design's priceFor(): largest tier first.
  function priceFor(n, unit = UNIT) {
    let rem = n, price = 0, groups = 0;
    TIERS.forEach(t => { const k = Math.floor(rem / t.size); price += k * (t.size - t.free) * unit; groups += k; rem -= k * t.size; });
    price += rem * unit;
    return { price, was: n * unit, groups };
  }

  /* ───── Events ───── */
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
  const on = (name, fn) => document.addEventListener(name, e => fn(e.detail, e));

  /* ───── Scroll lock + drawers ───── */
  let lastFocus = null;
  function lock(onOff) { document.documentElement.classList.toggle('is-locked', onOff); }
  function openDrawer(name) {
    const d = $(`[data-drawer="${name}"]`); if (!d) return;
    $$('[data-drawer].is-open').forEach(x => x !== d && closeDrawer(x.dataset.drawer));
    lastFocus = document.activeElement;
    d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false'); lock(true);
    $$(`[data-open="${name}"]`).forEach(b => b.setAttribute('aria-expanded', 'true'));
    setTimeout(() => { const f = $('[data-close].icon-btn, button', $('.drawer__panel', d)); f && f.focus({ preventScroll: true }); }, 50);
    emit('alma:drawer', { name, open: true });
  }
  function closeDrawer(name) {
    const d = $(`[data-drawer="${name}"]`); if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true');
    if (!$('[data-drawer].is-open') && !finder.isOpen()) lock(false);
    $$(`[data-open="${name}"]`).forEach(b => b.setAttribute('aria-expanded', 'false'));
    lastFocus && lastFocus.focus && lastFocus.focus({ preventScroll: true });
    emit('alma:drawer', { name, open: false });
  }

  function scrollToEl(el, offset = 16) {
    if (!el) return;
    // Scrolling up brings the sticky header back, so leave room for it above the target.
    const top = el.getBoundingClientRect().top;
    const hdr = top < 0 ? ($('.section-header') || {}).offsetHeight || 0 : 0;
    window.scrollTo({ top: top + window.scrollY - offset - hdr, behavior: 'smooth' });
  }

  // While the theme scrolls the page itself, the smart header may hide but never pops in.
  const headerHold = { until: 0 };
  // The hold lasts as long as the scroll itself: renewed on every scroll frame, released once scrolling has been
  // quiet for 150ms (long smooth scrolls, e.g. from the bottom of the page, take well over a second; Safari only
  // recently got `scrollend`, so this doesn't rely on it).
  function holdHeader() {
    let idle;
    const done = () => { headerHold.until = 0; window.removeEventListener('scroll', onScroll); };
    const onScroll = () => { headerHold.until = Infinity; clearTimeout(idle); idle = setTimeout(done, 150); };
    headerHold.until = performance.now() + 400; // until the smooth scroll has started
    window.addEventListener('scroll', onScroll, { passive: true });
    idle = setTimeout(done, 400);
  }

  // After a bundle pick: bring the Add to cart button and the payment badges under it into view, plus as much
  // of the opened card as fits. Scrolling down lets the header hide; scrolling up keeps it as it is (held).
  function frameBundle(card, box) {
    const atc = $('[data-atc]', box), pay = $('.pay-icons', box), wrap = $('.section-header');
    if (!card || !atc) return;
    const m = 16, vh = window.innerHeight;
    const top = card.getBoundingClientRect().top;
    const bottom = pay ? pay.getBoundingClientRect().bottom : atc.getBoundingClientRect().bottom + 4; // + its press shadow
    const hdr = wrap && !wrap.classList.contains('is-hidden') ? Math.max(0, wrap.getBoundingClientRect().bottom) : 0;
    let by = 0;
    if (bottom > vh - m) by = bottom - (vh - m);
    else if (top < hdr + m) by = Math.max(top - hdr - m, bottom - (vh - m));
    if (Math.abs(by) <= 1) return;
    holdHeader();
    window.scrollBy({ top: by, behavior: 'smooth' });
  }

  /* ───── Smart sticky header: hide on scroll down, show on scroll up ───── */
  function initHeader() {
    const wrap = $('.section-header'); if (!wrap || wrap.dataset.smart) return;
    wrap.dataset.smart = '1';
    // The logo scrolls back to the top on the home and product page (same content) instead of reloading;
    // elsewhere it's a normal link to the start page.
    const logo = $('[data-logo-top]', wrap);
    if (logo) logo.addEventListener('click', e => {
      if (!$('[data-buybox]') || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
    const root = document.documentElement;
    let lastY = window.scrollY, queued = false;
    const sync = () => {
      queued = false;
      const y = window.scrollY, h = wrap.offsetHeight;
      root.style.setProperty('--hdrH', h + 'px');
      // Until the header would leave the screen (announcement bar + header), it just sits in place.
      const natural = (wrap.previousElementSibling ? wrap.previousElementSibling.offsetHeight : 0) + h;
      if (root.classList.contains('is-locked')) { lastY = y; return; } // a drawer is open
      const held = performance.now() < headerHold.until; // the theme is scrolling the page (bundle pick)
      if (y <= natural) wrap.classList.remove('is-hidden');
      else if (y > lastY + (held ? 0 : 4)) wrap.classList.add('is-hidden');
      else if (!held && y < lastY - 4) wrap.classList.remove('is-hidden');
      if (held || Math.abs(y - lastY) > 4 || y <= natural) lastY = y;
      // Sticky things below (the product gallery and buy box) move down while the header is on screen.
      const shown = !wrap.classList.contains('is-hidden') && y > (natural - h);
      root.style.setProperty('--hdrShown', shown ? h + 'px' : '0px');
    };
    window.addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(sync); } }, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  /* ───── Dropdowns (delegated; owners listen for "ddchange") ───── */
  function closeDropdowns(except) {
    $$('.dd.is-open').forEach(dd => {
      if (dd === except) return;
      dd.classList.remove('is-open');
      $('[data-dd-list]', dd).hidden = true;
      $('[data-dd-btn]', dd).setAttribute('aria-expanded', 'false');
    });
  }
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-dd-btn]');
    const opt = e.target.closest('.dd__opt');
    if (btn) {
      const dd = btn.closest('.dd'), open = !dd.classList.contains('is-open');
      closeDropdowns(dd);
      dd.classList.toggle('is-open', open);
      $('[data-dd-list]', dd).hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      if (open) {
        const cur = $('.dd__opt.is-on', dd) || $('.dd__opt', dd); cur && cur.focus({ preventScroll: true });
        // Near the bottom of the cart drawer the list would sit under the sticky footer; bring it into view.
        requestAnimationFrame(() => $('[data-dd-list]', dd).scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
      }
      return;
    }
    if (opt) {
      if (opt.classList.contains('is-soldout')) return;
      const dd = opt.closest('.dd');
      $$('.dd__opt', dd).forEach(o => { o.classList.toggle('is-on', o === opt); o.setAttribute('aria-selected', String(o === opt)); });
      closeDropdowns();
      $('[data-dd-btn]', dd).focus({ preventScroll: true });
      dd.dispatchEvent(new CustomEvent('ddchange', { bubbles: true, detail: { value: opt.dataset.value, kind: dd.dataset.dd } }));
      return;
    }
    if (!e.target.closest('.dd')) closeDropdowns();
  });
  document.addEventListener('keydown', e => {
    const opt = e.target.closest && e.target.closest('.dd__opt');
    if (opt && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      const opts = $$('.dd__opt', opt.closest('.dd'));
      const i = opts.indexOf(opt) + (e.key === 'ArrowDown' ? 1 : -1);
      opts[(i + opts.length) % opts.length].focus();
    }
    if (e.key === 'Escape') {
      if ($('.dd.is-open')) { const b = $('.dd.is-open [data-dd-btn]'); closeDropdowns(); b && b.focus(); return; }
      if (finder.isOpen()) { finder.close(); return; }
      $$('[data-drawer].is-open').forEach(d => closeDrawer(d.dataset.drawer));
    }
  });

  /* ───── Global clicks ───── */
  document.addEventListener('click', e => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      e.preventDefault();
      const name = opener.dataset.open;
      if (name === 'finder') finder.open(); else openDrawer(name);
      return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer) { closeDrawer(closer.closest('[data-drawer]').dataset.drawer); return; }

    const action = e.target.closest('[data-menu-action]');
    if (action) {
      e.preventDefault(); closeDrawer('menu');
      const a = action.dataset.menuAction;
      setTimeout(() => {
        if (a === 'finder') finder.open();
        if (a === 'faq') { const f = $('#faq'); if (f) scrollToEl(f); else if (P) location.href = P.url + '#faq'; }
      }, 260);
      return;
    }
    // Menu links like "/#size-guide" or "/#guarantee" (footer menus are plain Shopify links):
    // open the size finder, or scroll when the target is on this page instead of reloading home.
    const hashLink = e.target.closest('a[href*="#"]');
    if (hashLink && new URL(hashLink.href).pathname.replace(/\/$/, '') === '' ) {
      const hash = new URL(hashLink.href).hash;
      if (hash === '#size-guide') { e.preventDefault(); finder.open(); return; }
      const target = hash.length > 1 && document.getElementById(hash.slice(1));
      if (target) { e.preventDefault(); scrollToEl(target); return; }
    }
    const toBuy = e.target.closest('[data-scroll-buy]');
    if (toBuy && $('[data-bundles]')) { e.preventDefault(); scrollToEl($('[data-bundles]')); return; }

    const tog = e.target.closest('[data-accordion-toggle]');
    if (tog) {
      const item = tog.closest('[data-accordion]');
      const open = tog.getAttribute('aria-expanded') !== 'true';
      // One open at a time within the same list, like the design.
      $$('[data-accordion]', item.parentElement).forEach(it => setAccordion(it, it === item && open));
      return;
    }
    const more = e.target.closest('[data-load-more]');
    if (more) {
      const sec = more.closest('[data-reviews]');
      $$('[data-review][hidden]', sec).slice(0, Number(sec.dataset.page) || 6).forEach(r => { r.hidden = false; });
      if (!$('[data-review][hidden]', sec)) more.hidden = true;
    }
  });
  function setAccordion(item, open) {
    const t = $('[data-accordion-toggle]', item), p = $('[data-accordion-panel]', item), s = $('[data-sign]', item);
    t.setAttribute('aria-expanded', String(open)); p.hidden = !open; if (s) s.textContent = open ? '–' : '+';
    item.classList.toggle('is-open', open); // card FAQ: green open state + turned chevron (base.css .faq__list--cards)
  }

  /* ───── Countdown to local midnight ───── */
  // Counts down to 23:00 Swedish time (CET/CEST), the same-day shipping cut-off, then starts on the next day's 23:00.
  const hmsFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Stockholm', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
  function initCountdown(root) {
    $$('[data-countdown]', root).forEach(el => {
      const tick = () => {
        const [h, m, s] = hmsFmt.format(new Date()).split(':').map(Number), now = (h % 24) * 3600 + m * 60 + s, cut = 23 * 3600;
        const t = now < cut ? cut - now : 86400 - now + cut, p = v => String(v).padStart(2, '0');
        const v = { h: p(Math.floor(t / 3600)), m: p(Math.floor(t % 3600 / 60)), s: p(t % 60) };
        $$('[data-t]', el).forEach(x => { x.textContent = v[x.dataset.t]; });
      };
      tick(); clearInterval(el._timer); el._timer = setInterval(tick, 1000);
    });
  }

  /* ───── Headline: one line, scaled to the column (design's measureTitle) ───── */
  function initTitle(root) {
    $$('[data-fit-title]', root).forEach(h => {
      const measure = () => {
        const sp = h.firstElementChild, fs = parseFloat(getComputedStyle(h).fontSize) || 0, sw = sp ? sp.offsetWidth : 0;
        if (fs && sw) h.style.setProperty('--titleDiv', String((sw / fs) * 1.01));
      };
      measure(); setTimeout(measure, 400);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    });
  }

  /* ───── Gallery ───── */
  function initGallery(root) {
    $$('[data-gallery]', root).forEach(g => {
      const slides = $$('[data-slide]', g), thumbs = $$('[data-thumb]', g.parentElement), strip = $('[data-thumbs]', g.parentElement);
      let i = 0, sx = null, sy = 0;
      // From the 3rd photo on, the strip slides along with the gallery: the active thumbnail sits second, with most
      // of the previous one still visible (clamped at both ends). Scrolls the strip only, never the page.
      const follow = () => {
        const t = thumbs[i], max = strip ? strip.scrollWidth - strip.clientWidth : 0; if (!t || max <= 1) return;
        const step = t.offsetWidth + parseFloat(getComputedStyle(strip).columnGap || 8);
        const target = strip.scrollLeft + (t.getBoundingClientRect().left - strip.getBoundingClientRect().left) - step * 1.3;
        strip.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: 'smooth' });
      };
      const go = n => {
        i = (n + slides.length) % slides.length;
        slides.forEach((s, k) => s.classList.toggle('is-active', k === i));
        thumbs.forEach((t, k) => t.classList.toggle('is-active', k === i));
        follow();
      };
      thumbs.forEach(t => t.addEventListener('click', () => go(Number(t.dataset.thumb))));
      g.addEventListener('pointerdown', e => { sx = e.clientX; sy = e.clientY; });
      g.addEventListener('pointercancel', () => { sx = null; });
      g.addEventListener('pointerup', e => {
        if (sx == null) return;
        const dx = e.clientX - sx, dy = e.clientY - sy; sx = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        go(dx < 0 ? i + 1 : i - 1);
      });
    });
  }

  /* ───── Buy box ───── */
  const buyboxes = new Set();
  function initBuybox(root) {
    $$('[data-buybox]', root).forEach(box => {
      const bundles = $$('[data-bundle]', box);
      const panel = $('[data-panel]', box);
      const atc = $('[data-atc]', box);
      const err = $('[data-size-error]', box);
      const rows = $$('[data-row]', box);
      const sticky = box.parentElement.querySelector('[data-sticky-atc]');
      const st = { bundle: Math.max(0, bundles.findIndex(b => b.classList.contains('is-on'))) };
      const tanks = rows.map(r => ({ colour: ($('[data-dd="colour"] .dd__opt.is-on', r) || {}).dataset?.value || COLOURS[0] || null, size: null }));
      const qty = () => Number(bundles[st.bundle]?.dataset.qty || 1);

      function renderRow(i) {
        const r = rows[i], t = tanks[i]; if (!r) return;
        const thumb = $('[data-row-thumb]', r);
        if (thumb) {
          thumb.style.backgroundColor = hexOf(t.colour);
          const src = imgFor(t.colour);
          thumb.innerHTML = src ? `<img src="${esc(src)}" alt="Linne i ${esc(t.colour)}" width="48" height="48">` : '';
        }
        const cdd = $('[data-dd="colour"]', r);
        if (cdd) {
          $('[data-dd-label]', cdd).textContent = t.colour;
          $('[data-dd-dot]', cdd).style.background = hexOf(t.colour);
          $('[data-dd-btn]', cdd).setAttribute('aria-label', `Linne ${i + 1}, färg: ${t.colour}`);
        }
        const sdd = $('[data-dd="size"]', r);
        if (sdd) {
          const btn = $('[data-dd-btn]', sdd);
          $('[data-dd-label]', sdd).textContent = t.size || 'Välj storlek';
          btn.classList.toggle('is-empty', !t.size);
          btn.setAttribute('aria-label', `Linne ${i + 1}, storlek: ${t.size || 'inte vald'}`);
          $$('.dd__opt', sdd).forEach(o => {
            const v = variantFor(t.colour, o.dataset.value), out = v && !v.available;
            o.classList.toggle('is-soldout', !!out); o.setAttribute('aria-disabled', String(!!out));
            o.classList.toggle('is-on', o.dataset.value === t.size); o.setAttribute('aria-selected', String(o.dataset.value === t.size));
          });
        }
      }
      function clearError() { err.hidden = true; rows.forEach(r => { const b = $('[data-dd="size"] [data-dd-btn]', r); b && b.classList.remove('is-error'); }); }

      function select(i) {
        st.bundle = i;
        const b = bundles[i], n = qty();
        bundles.forEach((x, k) => { x.classList.toggle('is-on', k === i); $('[data-bundle-select]', x).setAttribute('aria-pressed', String(k === i)); });
        $('[data-bundle-body]', b).appendChild(panel);
        rows.forEach((r, k) => { r.hidden = k >= n; });
        $$('[data-perks]', panel).forEach(p => { p.hidden = (p.dataset.perks === 'bundle') !== (n > 1); });
        $('[data-atc-count]', box).textContent = n === 1 ? '1 linne' : `${n} linnen`;
        clearError(); closeDropdowns();
      }
      // A shopper's own pick also scrolls the Add to cart button, its badges and the opened card into frame.
      bundles.forEach((b, i) => $('[data-bundle-select]', b).addEventListener('click', () => { select(i); frameBundle(b, box); }));

      box.addEventListener('ddchange', e => {
        const row = e.target.closest('[data-row]'); if (!row) return;
        const i = Number(row.dataset.row), { value, kind } = e.detail;
        if (kind === 'colour') tanks[i].colour = value;
        if (kind === 'size') {
          tanks[i].size = value;
          // Like the design: the first size picked fills every tank still empty.
          tanks.forEach(t => { if (!t.size) t.size = value; });
          rows.forEach((_, k) => renderRow(k));
        }
        renderRow(i); clearError();
      });

      async function addToCart() {
        const n = qty(), chosen = tanks.slice(0, n);
        if (P && SI >= 0 && chosen.some(t => !t.size)) {
          err.textContent = n === 1 ? 'Välj din storlek först.' : 'Välj storlek för varje linne.';
          err.hidden = false;
          rows.slice(0, n).forEach((r, k) => { const b = $('[data-dd="size"] [data-dd-btn]', r); if (b && !chosen[k].size) b.classList.add('is-error'); });
          clearTimeout(box._hl); box._hl = setTimeout(() => rows.forEach(r => { const b = $('[data-dd="size"] [data-dd-btn]', r); b && b.classList.remove('is-error'); }), 2400);
          scrollToEl($('[data-rows]', box));
          return;
        }
        const items = {};
        for (const t of chosen) {
          const v = variantFor(t.colour, t.size);
          if (!v) { err.textContent = 'Den här kombinationen finns inte.'; err.hidden = false; return; }
          if (!v.available) { err.textContent = `${t.colour} ${t.size || ''} är slutsåld. Välj en annan färg eller storlek.`; err.hidden = false; return; }
          items[v.id] = (items[v.id] || 0) + 1;
        }
        atc.disabled = true;
        try {
          await cart.add(Object.entries(items).map(([id, quantity]) => ({ id: Number(id), quantity })));
          openDrawer('cart');
        } catch (ex) {
          err.textContent = ex.message || 'Det gick inte att lägga i varukorgen. Försök igen.'; err.hidden = false;
        } finally { atc.disabled = false; }
      }
      atc.addEventListener('click', addToCart);

      const api = {
        box, addToCart, select,
        setAllSizes(size) { tanks.forEach(t => { t.size = size; }); rows.forEach((_, k) => renderRow(k)); clearError(); },
        // Bring the selected bundle card (now showing its sizes) and the Add to cart button into view.
        frame() { frameBundle(bundles[st.bundle], box); }
      };
      buyboxes.add(api);

      // Sticky bar: shows once the main button has scrolled above the viewport, but hides while any other
      // buy button ([data-atc], [data-cta]: e.g. "Make the Switch", the final CTA) is on screen.
      // A scroll listener, not an IntersectionObserver: IO only fires on crossings,
      // so a fast jump past the button (flick, #faq link) never showed the bar.
      if (sticky) {
        const btn = $('[data-sticky-atc-btn]', sticky);
        btn.addEventListener('click', addToCart);
        const ctas = $$('[data-atc], [data-cta]');
        const ctaOnScreen = () => ctas.some(c => { const r = c.getBoundingClientRect(); return r.height > 0 && r.bottom > 0 && r.top < window.innerHeight; });
        let queued = false;
        const sync = () => {
          queued = false;
          const show = atc.getBoundingClientRect().bottom < 0 && !ctaOnScreen() && !$('[data-drawer="cart"].is-open');
          if (show === sticky.classList.contains('is-shown')) return;
          sticky.classList.toggle('is-shown', show); sticky.setAttribute('aria-hidden', String(!show)); btn.tabIndex = show ? 0 : -1;
          // The footer keeps room for the bar only while it shows (base.css: html.has-sticky-atc .footer).
          document.documentElement.style.setProperty('--stickyH', sticky.offsetHeight + 'px');
          document.documentElement.classList.toggle('has-sticky-atc', show);
        };
        const queue = () => { if (!queued) { queued = true; requestAnimationFrame(sync); } };
        window.addEventListener('scroll', queue, { passive: true });
        window.addEventListener('resize', queue);
        on('alma:drawer', sync);
        sync();
      }
      rows.forEach((_, k) => renderRow(k));
    });
  }
  // "Make The Switch" buttons (comparison, final CTA): select that bundle and bring its card + Add to cart into view.
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-select-bundle]'); if (!b) return;
    const bb = [...buyboxes][0];
    if (!bb) { if (P) { e.preventDefault(); location.href = P.url + '?bundle=' + ((Number(b.dataset.selectBundle) || 0) + 1); } return; }
    e.preventDefault();
    bb.select(Number(b.dataset.selectBundle) || 0);
    bb.frame();
  });
  on('alma:size', ({ size }) => buyboxes.forEach(b => b.setAllSizes(size)));

  /* ───── Cart ───── */
  const cart = (() => {
    const drawer = $('[data-drawer="cart"]');
    const scroller = drawer && $('[data-cart-scroll]', drawer);
    const foot = drawer && $('[data-cart-foot]', drawer);
    const ui = { ec: null, ecErr: false, fw: null, up: null, upEdit: false, error: '' };
    let data = null, fetchedAt = 0;

    // Every cart request carries the page's country. Without it, Shopify creates the cart in the geo-IP country:
    // a visitor outside every market (e.g. NL) on a ?country=ES page got a Sweden-market cart in euros (40,45, not 39,95).
    const withCountry = url => CFG.country ? url + (url.includes('?') ? '&' : '?') + 'country=' + encodeURIComponent(CFG.country) : url;
    const req = async (url, body) => {
      url = withCountry(url);
      const r = await fetch(url, body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : { headers: { Accept: 'application/json' } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.description || j.message || 'Något gick fel. Försök igen.');
      return j;
    };
    async function run(fn) {
      drawer && $('.cart', drawer).classList.add('is-busy');
      ui.error = '';
      try { await fn(); } catch (ex) { ui.error = ex.message; }
      try { data = await req(CFG.routes.cart); fetchedAt = Date.now(); } catch (ex) { ui.error = ui.error || ex.message; }
      drawer && $('.cart', drawer).classList.remove('is-busy');
      render();
    }
    // Throws so the buy box can show the error next to its button.
    async function add(items) {
      let failed = null;
      await run(() => req(CFG.routes.cartAdd, { items }).catch(ex => { failed = ex; throw ex; }));
      if (failed) { ui.error = ''; render(); throw failed; }
    }
    const update = updates => run(() => req(CFG.routes.cartUpdate, { updates }));
    const change = (key, quantity) => run(() => req(CFG.routes.cartChange, { id: key, quantity }));

    const isTank = it => P && it.product_id === P.id;
    const vOpts = it => { const v = P && P.variants.find(x => x.id === it.variant_id); return v ? v.options : (it.options_with_values || []).map(o => o.value); };
    const colourOf = it => (CI >= 0 ? vOpts(it)[CI] : '');
    const sizeOf = it => (SI >= 0 ? vOpts(it)[SI] : '');
    const tankCount = () => (data ? data.items.filter(isTank).reduce((a, it) => a + it.quantity, 0) : 0);
    // The size most tanks in the cart have (counted per tank, not per line). Ties go to the size added
    // most recently: /cart.js lists the newest line first. Used to pre-select the free tank and the upgrade.
    const majoritySize = () => {
      const count = new Map();
      data.items.filter(isTank).forEach(it => { const z = sizeOf(it); if (z) count.set(z, (count.get(z) || 0) + it.quantity); });
      let best = null; count.forEach((c, z) => { if (best === null || c > count.get(best)) best = z; });
      return best;
    };

    const ICON_TAG = '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" fill="#4A4E4C"/><circle cx="7.5" cy="7.5" r="1.6" fill="#E8EFEA"/></svg>';
    const ICON_TRASH = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>';
    const ICON_CHECK = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2E7D4F" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
    const ICON_RULER = '<svg class="icon-ruler" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><g transform="rotate(-45 12 12)"><rect x="2.5" y="11.5" width="19" height="6" rx="1.2"/><path d="M5.5 11.5v2.6M8.5 11.5v1.5M11.5 11.5v2.6M14.5 11.5v1.5M17.5 11.5v2.6"/><path d="M2.5 7.5h19M2.5 6v3M21.5 6v3"/></g></svg>';
    const CHEV = '<svg class="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4A4E4C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
    const TRUST_ICONS = [
      '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
      '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
      '<path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/>'
    ];
    const trustHTML = () => `<div class="cart__trust">${(CFG.trust || []).map((t, i) => `<div><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1F4A35" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${TRUST_ICONS[i] || ''}</svg><span>${String(t).split('|').map(x => `<span>${esc(x)}</span>`).join('')}</span></div>`).join('')}</div>`;
    const thumbHTML = (colour, src, plain) => `<span class="cart__thumb${plain ? ' cart__thumb--plain' : ''}" style="background:${esc(hexOf(colour))}">${src ? `<img src="${esc(src)}" alt="" width="64" height="64" loading="lazy">` : ''}</span>`;
    // Same markup as the buy box rows (snippets/buy-panel.liquid), so cart dropdowns look identical.
    // scope/i end up as data-<scope>="<i>": ec = empty-cart 3-pack, fw = free tank, up = upgrade extras.
    const ddHTML = (kind, label, cur, opts, scope, i, name, isErr) => `
      <div class="dd${kind === 'size' ? ' dd--right' : ''}" data-dd="${kind}" data-${scope}="${i}">
        <button class="dd__btn${kind === 'size' ? ' dd__btn--size' : ''}${kind === 'size' && !cur ? ' is-empty' : ''}${isErr ? ' is-error' : ''}" type="button" aria-haspopup="listbox" aria-expanded="false" aria-label="${kind === 'size' ? 'Storlek' : 'Färg'}, ${esc(name)}" data-dd-btn>
          ${kind === 'colour' ? `<span class="dot" style="background:${esc(hexOf(cur))}"></span>` : ''}<span class="dd__label">${esc(cur || label)}</span>${CHEV}
        </button>
        <div class="dd__list" role="listbox" hidden data-dd-list>
          ${opts.map(o => `<button class="dd__opt${o === cur ? ' is-on' : ''}" type="button" role="option" aria-selected="${o === cur}" data-value="${esc(o)}">${kind === 'colour' ? `<span class="dot dot--lg" style="background:${esc(hexOf(o))}"></span>` : ''}${esc(o)}</button>`).join('')}
        </div>
      </div>`;
    const rowHTML = (t, scope, i, name, isErr) => `
      <div class="tank-row">
        <span class="tank-row__thumb" style="background-color:${esc(hexOf(t.colour))}">${imgFor(t.colour) ? `<img src="${esc(imgFor(t.colour))}" alt="Linne i ${esc(t.colour)}" width="48" height="48">` : ''}</span>
        ${CI >= 0 ? ddHTML('colour', '', t.colour, CART_COLOURS.length ? CART_COLOURS : COLOURS, scope, i, name) : '<span></span>'}
        ${SI >= 0 ? ddHTML('size', 'Välj storlek', t.size, SIZES, scope, i, name, isErr) : ''}
      </div>`;

    function emptyHTML() {
      const n = 3, pf = priceFor(n);
      ui.ec = ui.ec || CART_COLOURS.slice(0, n).map(c => ({ colour: c, size: null }));
      while (ui.ec.length < n) ui.ec.push({ colour: CART_COLOURS[0] || COLOURS[0], size: null });
      const rows = !P ? '' : ui.ec.map((t, i) => rowHTML(t, 'ec', i, `Linne ${i + 1}`, ui.ecErr && !t.size)).join('');
      const perks = ['Fri frakt idag', '90 dagars passformsgaranti', 'Gratis tvättpåse från Alma'];
      const img = imgFor(CART_COLOURS[0]) || (P && P.image);
      return `
        <div class="cart__empty">
          <strong class="cart__empty-title">Din varukorg är tom</strong>
          ${P ? `<div class="ec">
            <div class="ec__badge">Mest populär · Spara ${money(pf.was - pf.price)}</div>
            <div class="ec__body">
              <div class="ec__head">
                <span class="ec__img">${img ? `<img src="${esc(img)}" alt="" width="64" height="64">` : ''}</span>
                <div class="cart__info"><strong class="cart__name">3 för 2</strong><span class="cart__variant" style="white-space:nowrap">${n} linnen · ${money(perTank(pf.price / n))}/st</span></div>
                <span class="ec__prices"><strong>${money(pf.price)}</strong><s>${money(pf.was)}</s></span>
              </div>
              <div class="panel__rows" data-ec-rows>${rows}</div>
              <div class="panel__help" style="margin-top:-4px"><button class="link-btn link-btn--icon" type="button" data-open="finder">${ICON_RULER}<span>Hitta min storlek</span></button></div>
              <ul class="perks perks--sm" aria-label="Ingår i paketet">${perks.map(p => `<li>${ICON_CHECK}<span>${p}</span></li>`).join('')}</ul>
              ${ui.ecErr ? '<span class="ec__err" role="alert">Välj storlek för varje linne</span>' : ''}
              <button class="btn btn--md" type="button" data-press data-ec-add>Lägg ${n} linnen i varukorgen · ${money(pf.price)}</button>
            </div>
          </div>
          <button class="btn-ghost" type="button" data-ec-shop>Välj mitt paket</button>` : ''}
          <div class="cart__trust-bar">${trustHTML()}</div>
        </div>`;
    }

    function render() {
      if (!drawer || !data) return;
      const n = tankCount(), items = data.items;
      $$('[data-cart-count]').forEach(el => { el.textContent = data.item_count; el.hidden = data.item_count === 0; });
      $$('[data-open="cart"]').forEach(el => el.setAttribute('aria-label', `Öppna varukorgen, ${data.item_count} ${data.item_count === 1 ? 'vara' : 'varor'}`));
      $('[data-cart-total]', drawer).textContent = data.item_count;

      if (!items.length) { ui.up = null; scroller.innerHTML = emptyHTML() + (ui.error ? `<p class="cart__error" role="alert">${esc(ui.error)}</p>` : ''); foot.hidden = true; foot.innerHTML = ''; return; }

      const bundle = n >= MIN_BUNDLE;
      const shipFee = bundle ? 0 : SHIP_FEE;
      const disc = data.total_discount;
      // Bundles are line-level (BXGY) discounts; promos stack on top as order-level ones.
      const bundleDisc = items.reduce((a, it) => a + it.original_line_price - it.final_line_price, 0);
      const promos = data.cart_level_discount_applications || [];
      const groups = priceFor(n).groups;

      // Item cards: what Shopify actually discounted decides FREE vs paid.
      const paid = [], free = [];
      items.forEach(it => {
        const unit = it.original_price;
        const lineDisc = it.original_line_price - it.final_line_price;
        const freeQty = unit > 0 ? Math.min(it.quantity, Math.floor((lineDisc + 1) / unit)) : 0;
        const colour = colourOf(it), size = sizeOf(it), tank = isTank(it);
        // Tanks without a colour photo show their swatch colour, like the design; never another colour's photo.
        const src = tank ? imgFor(colour) : it.image;
        const variant = tank ? [colour, size].filter(Boolean).join(' · ') : (it.variant_title || '');
        const paidQty = it.quantity - freeQty;
        if (paidQty > 0) paid.push(`
          <div class="cart__card">
            ${thumbHTML(colour, src, !tank)}
            <div class="cart__info"><strong class="cart__name">${esc(it.product_title)}</strong><span class="cart__variant">${esc(variant)}</span></div>
            <span class="cart__right">
              <button class="cart__trash" type="button" data-remove="${esc(it.key)}" data-qty="${it.quantity}" aria-label="Ta bort ett ${esc(it.product_title)}, ${esc(variant)}">${ICON_TRASH}</button>
              <span class="cart__price">${paidQty > 1 ? `<small>× ${paidQty}</small>` : ''}<strong>${money(freeQty ? unit * paidQty : it.final_line_price)}</strong></span>
            </span>
          </div>`);
        if (freeQty > 0) free.push(`
          <div class="cart__card">
            ${thumbHTML(colour, src, !tank)}
            <div class="cart__info"><strong class="cart__name">${esc(it.product_title)}${freeQty > 1 ? ` × ${freeQty}` : ''}</strong><span class="cart__variant">${esc(variant)}</span><span class="chip">${ICON_TAG}Paketrabatt</span></div>
            <span class="cart__free"><span class="cart__free-tag">GRATIS</span><s>${money(unit * freeQty)}</s></span>
          </div>`);
      });

      // Promos, as in the design: complete a tier for free, or upgrade by two.
      let promo = '';
      const first = items.find(isTank);
      const fc = first ? colourOf(first) : '';
      const nextTier = TIERS.slice().reverse().find(t => t.size === n + 1 && t.free >= 1);
      if (P && nextTier) {
        // Follow the cart on every render (majority size), unless the customer picked something here.
        ui.fw = ui.fw || {};
        if (!ui.fw.pickedSize) ui.fw.size = majoritySize() || SIZES[0];
        if (!ui.fw.pickedColour) ui.fw.colour = CART_COLOURS.includes(fc) ? fc : CART_COLOURS[0];
        promo = `
          <div class="cart__promo">
            <div class="cart__promo-text"><strong class="cart__promo-title">Ditt ${n + 1}:e linne är GRATIS</strong><span class="cart__promo-line">Välj färg så är det ditt, utan extra kostnad.</span></div>
            <div class="panel__rows">${rowHTML(ui.fw, 'fw', 0, 'ditt gratis linne')}</div>
            <button class="btn-ghost" type="button" data-fw-add>Lägg till gratis linne</button>
          </div>`;
      } else if (P && ui.up && ui.up.n === n) {
        const doneText = n >= 5 ? 'Uppgraderat till 5 linnen' : 'Uppgraderat till 3-pack';
        promo = ui.upEdit ? `
          <div class="cart__promo">
            <div class="cart__promo-text"><strong class="cart__promo-title">✓ ${doneText}</strong><span class="cart__promo-line">Välj färger för dina 2 extra linnen, eller behåll våra val.</span></div>
            <div class="panel__rows">${ui.up.extras.map((x, i) => rowHTML(x, 'up', i, `Linne ${n - 1 + i}`)).join('')}</div>
            <button class="text-link" type="button" style="align-self:flex-end" data-up-toggle aria-expanded="true">Klar</button>
          </div>` : `
          <div class="cart__promo"><div class="cart__promo-split"><strong class="cart__promo-title">✓ ${doneText}</strong><button class="text-link" type="button" data-up-toggle aria-expanded="false">Ändra färger</button></div></div>`;
      } else if (P && n < MIN_BUNDLE && TIERS.some(t => t.size === n + 2)) {
        // Only below the 3-pack (1 tank → "Uppgradera till 3-pack"); a cart with the 3-pack gets no upsell.
        const extra = priceFor(n + 2).price - priceFor(n).price;
        promo = `
          <div class="cart__promo cart__promo--row">
            <img src="${esc(imgFor(CART_COLOURS[0]) || P.image || '')}" alt="" width="48" height="48">
            <div class="cart__promo-text"><strong class="cart__promo-title">${n === 1 ? 'Uppgradera till 3-pack' : `Gör det till ${n + 2} linnen`}</strong><span class="cart__promo-line">2 linnen för ${money(extra)}</span><span class="cart__promo-line">${n + 2 >= MIN_BUNDLE && n < MIN_BUNDLE ? '+ fri frakt' : `${money(perTank(extra / 2))}/st`}</span></div>
            <button class="btn-ghost" type="button" data-upgrade>Uppgradera</button>
          </div>`;
      }

      const off = disc > 0;
      scroller.innerHTML = `
        ${n > 0 ? `<div class="cart__band">
          <span class="cart__band-text">${bundle ? 'Grattis! Du har fått <strong>fri frakt!</strong>' : `Välj ${MIN_BUNDLE}-pack så får du <strong>fri frakt!</strong>`}</span>
          <span class="cart__progress"><span style="width:${bundle ? 100 : Math.round(100 * n / MIN_BUNDLE)}%"></span></span>
        </div>` : ''}
        ${bundleDisc > 0 ? `<div class="cart__offer">${groups > 1 ? 'Paketrabatter dras automatiskt' : 'Paketrabatt dras automatiskt'}</div>` : ''}
        ${ui.error ? `<p class="cart__error" role="alert">${esc(ui.error)}</p>` : ''}
        <div class="cart__items">${paid.join('')}${free.join('')}</div>
        ${promo}
        <div class="cart__summary">
          <strong class="cart__summary-title">Sammanfattning</strong>
          <div class="cart__line"><span>Delsumma</span><span>${money(data.original_total_price)}</span></div>
          ${bundleDisc > 0 ? `<div class="cart__line"><span>Rabatt</span><span class="cart__green">−${money(bundleDisc)}</span></div><div><span class="chip">${ICON_TAG}Paketrabatt</span></div>` : ''}
          ${promos.map(pr => `<div class="cart__line"><span>${esc(pr.title)}</span><span class="cart__green">−${money(pr.total_allocated_amount)}</span></div>`).join('')}
          <div class="cart__line"><span>Frakt</span>${shipFee ? `<span>${money(shipFee)}</span>` : '<span class="cart__green cart__green--up">Fri frakt</span>'}</div>
        </div>`;

      const total = data.total_price + shipFee;
      foot.hidden = false;
      foot.innerHTML = `
        ${shipFee ? `<div class="cart__ship-line"><span>Frakt</span><span>${money(shipFee)}</span></div>` : ''}
        <div class="cart__total"><span>Totalt</span><span class="cart__total-right">
          ${off ? `<span class="cart__pct">−${Math.round(100 * disc / data.original_total_price)} %</span><span class="cart__was">${money(data.original_total_price + shipFee)}</span>` : ''}
          <strong>${money(total)}</strong></span></div>
        <button class="btn btn--md" type="submit" name="checkout" data-press style="margin-top:4px;font-size:17px">Till kassan<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></button>
        ${($('template[data-pay-icons]', drawer) || {}).innerHTML || ''}
        ${trustHTML()}`;
      requestAnimationFrame(shadow);
    }
    function shadow() { if (!scroller) return; foot.classList.toggle('has-more', scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 1); }

    if (drawer) {
      scroller.addEventListener('scroll', shadow, { passive: true });
      drawer.addEventListener('click', e => {
        const rm = e.target.closest('[data-remove]');
        if (rm) { change(rm.dataset.remove, Number(rm.dataset.qty) - 1); return; }
        if (e.target.closest('[data-fw-add]')) {
          const v = variantFor(ui.fw.colour, ui.fw.size);
          if (v) { ui.fw = null; run(() => req(CFG.routes.cartAdd, { items: [{ id: v.id, quantity: 1 }] })); }
          return;
        }
        if (e.target.closest('[data-upgrade]')) {
          const tanks = data.items.filter(isTank);
          const used = tanks.map(colourOf).filter(c => CART_COLOURS.includes(c));
          const size = majoritySize() || SIZES[0];
          const extras = [0, 1].map(i => ({ colour: used[i] || CART_COLOURS[i] || CART_COLOURS[0], size }));
          const n = tankCount() + 2;
          const items = {};
          extras.forEach(x => { const v = variantFor(x.colour, x.size); if (v) items[v.id] = (items[v.id] || 0) + 1; });
          ui.up = { n, extras }; ui.upEdit = true;
          run(() => req(CFG.routes.cartAdd, { items: Object.entries(items).map(([id, quantity]) => ({ id: Number(id), quantity })) }));
          return;
        }
        if (e.target.closest('[data-up-toggle]')) { ui.upEdit = !ui.upEdit; render(); return; }
        if (e.target.closest('[data-ec-add]')) {
          if (ui.ec.some(t => !t.size)) { ui.ecErr = true; render(); return; }
          const items = {};
          ui.ec.forEach(t => { const v = variantFor(t.colour, t.size); if (v) items[v.id] = (items[v.id] || 0) + 1; });
          ui.ec = null; ui.ecErr = false;
          run(() => req(CFG.routes.cartAdd, { items: Object.entries(items).map(([id, quantity]) => ({ id: Number(id), quantity })) }));
          return;
        }
        if (e.target.closest('[data-ec-shop]')) {
          closeDrawer('cart');
          const bb = [...buyboxes][0];
          if (bb) setTimeout(() => { bb.select(1); scrollToEl($('[data-bundles]', bb.box)); }, 380);
          else if (P) location.href = P.url;
        }
      });
      drawer.addEventListener('ddchange', e => {
        const dd = e.target.closest('[data-ec],[data-fw],[data-up]'); if (!dd) return;
        const { value, kind } = e.detail;
        if (dd.dataset.fw != null) { ui.fw[kind] = value; ui.fw[kind === 'size' ? 'pickedSize' : 'pickedColour'] = true; render(); return; }
        if (dd.dataset.up != null) {
          // Swap the extra tank already in the cart for the newly chosen variant.
          const x = ui.up.extras[Number(dd.dataset.up)];
          const before = variantFor(x.colour, x.size);
          x[kind] = value;
          const after = variantFor(x.colour, x.size);
          if (!before || !after || before.id === after.id) { render(); return; }
          const qtyOf = id => data.items.filter(it => it.variant_id === id).reduce((a, it) => a + it.quantity, 0);
          update({ [before.id]: Math.max(0, qtyOf(before.id) - 1), [after.id]: qtyOf(after.id) + 1 });
          return;
        }
        const i = Number(dd.dataset.ec);
        if (kind === 'colour') ui.ec[i].colour = value;
        if (kind === 'size') { ui.ec[i].size = value; ui.ec.forEach(t => { if (!t.size) t.size = value; }); }
        if (ui.ecErr) ui.ecErr = ui.ec.some(t => !t.size);
        render();
      });
      on('alma:size', ({ size }) => { if (ui.ec) { ui.ec.forEach(t => { t.size = size; }); ui.ecErr = false; render(); } });
      on('alma:drawer', ({ name, open }) => { if (name === 'cart' && open && Date.now() - fetchedAt > 1500) run(() => Promise.resolve()); });
    }

    return { add, refresh: () => run(() => Promise.resolve()), render };
  })();

  /* ───── Size finder (design's placeholder mapping; replace with the supplier chart) ───── */
  const finder = (() => {
    const root = $('[data-finder]');
    const sheet = root && $('[data-finder-sheet]', root);
    const body = root && $('[data-finder-body]', root);
    const F_SYS = {
      EU: { bands: [65, 70, 75, 80, 85, 90, 95, 100, 105, 110], cups: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'], eu: b => b },
      UK: { bands: [30, 32, 34, 36, 38, 40, 42, 44, 46, 48], cups: ['A', 'B', 'C', 'D', 'DD', 'E', 'F', 'FF', 'G', 'GG', 'H'], eu: b => 70 + (b - 32) * 2.5 }
    };
    const SZ = SIZES.length ? SIZES : ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];
    const s = { step: 0, sys: 'EU', band: null, cup: null, know: false, meas: '', unit: 'cm' };
    const bustOf = (sys, bi, ci) => { const S = F_SYS[sys]; return S.eu(S.bands[bi]) + 13 + 2 * ci; };
    const idxOf = cm => Math.max(0, Math.min(SZ.length - 1, Math.floor((cm - 80) / 8)));
    function calc() {
      const v = parseFloat(String(s.meas).replace(',', '.')), cm = s.unit === 'in' ? v * 2.54 : v;
      if (s.know && cm >= 60 && cm <= 170) { const i = idxOf(cm); return { ok: true, idx: i, size: SZ[i] }; }
      if (s.band != null && s.cup != null) { const i = idxOf(bustOf(s.sys, s.band, s.cup)); return { ok: true, idx: i, size: SZ[i] }; }
      return { ok: false, idx: -1, size: SZ[1] };
    }
    function fits(i, sys) {
      const S = F_SYS[sys], mid = 80 + i * 8 + 3.5, out = [];
      S.bands.forEach((b, bi) => S.cups.forEach((c, ci) => { const x = bustOf(sys, bi, ci); if (idxOf(x) === i) out.push({ t: b + c, d: Math.abs(x - mid), b }); }));
      return out.sort((p, q) => p.d - q.d || q.b - p.b).slice(0, 3).sort((p, q) => p.b - q.b).map(o => o.t).join(', ') || '—';
    }
    const bust = (i, unit) => { const lo = 80 + i * 8, hi = lo + 7; return unit === 'in' ? `${Math.round(lo / 2.54)}–${Math.round(hi / 2.54)} tum` : `${lo}–${hi} cm`; };
    const seg = (key, label, opts, cur) => `<div class="seg" role="group" aria-label="${label}">${opts.map(([k, l]) => `<button type="button" class="${cur === k ? 'is-on' : ''}" aria-pressed="${cur === k}" data-f="${key}" data-v="${k}">${l}</button>`).join('')}</div>`;

    function render() {
      if (!body) return;
      const F = calc();
      $('[data-finder-title]', root).textContent = s.step === 0 ? 'Hitta din storlek' : 'Ditt resultat';
      if (s.step === 0) {
        const S = F_SYS[s.sys];
        body.innerHTML = `
          <div style="display:flex">${seg('System', 'Storlekssystem', [['EU', 'EU'], ['UK', 'UK & USA']], s.sys)}</div>
          <span class="finder__label">Vilken bandvidd har du?</span>
          <div class="finder__grid">${S.bands.map((b, i) => `<button type="button" class="pick${s.band === i ? ' is-on' : ''}" aria-pressed="${s.band === i}" data-band="${i}">${b}</button>`).join('')}</div>
          <span class="finder__label">Vilken kupstorlek har du?</span>
          <div class="finder__grid">${S.cups.map((c, i) => `<button type="button" class="pick${s.cup === i ? ' is-on' : ''}" aria-pressed="${s.cup === i}" data-cup="${i}">${c}</button>`).join('')}</div>
          <div class="finder__know">
            <button class="finder__know-btn" type="button" aria-expanded="${s.know}" data-know><span>Jag vet inte min bh-storlek</span><span aria-hidden="true">${s.know ? '−' : '+'}</span></button>
            ${s.know ? `<div class="finder__measure">
              <svg width="60" height="74" viewBox="0 0 72 88" fill="none" aria-hidden="true" style="flex:0 0 auto"><path d="M22 4c0 8-8 10-14 14v66M50 4c0 8 8 10 14 14v66" stroke="#1A1C1B" stroke-width="1.5" stroke-linecap="round"/><path d="M14 40c6 8 16 8 22 0c6 8 16 8 22 0" stroke="#1A1C1B" stroke-width="1.2" stroke-linecap="round" opacity=".5"/><path d="M6 38h60" stroke="#1F4A35" stroke-width="2" stroke-dasharray="4 3"/><path d="M10 34l-4 4 4 4M62 34l4 4-4 4" stroke="#1F4A35" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <div class="finder__measure-fields">
                <label for="fMeas">Mät runt den fylligaste delen av bysten</label>
                <div class="finder__measure-row">
                  <input id="fMeas" class="finder__input" type="number" inputmode="decimal" min="0" value="${esc(s.meas)}" placeholder="${s.unit === 'cm' ? 't.ex. 98' : 't.ex. 38'}" data-meas>
                  ${seg('Unit', 'Måttenhet', [['cm', 'cm'], ['in', 'tum']], s.unit)}
                </div>
              </div>
            </div>` : ''}
          </div>
          <button class="btn finder__find" type="button" data-press data-find ${F.ok ? '' : 'disabled'}>Hitta min storlek</button>`;
      } else {
        body.innerHTML = `
          <div class="finder__result">
            <span class="finder__result-label">Din storlek</span>
            <span class="finder__result-size">${esc(F.size)}</span>
            <p>Sitter tätt och slätar ut. Mellan två storlekar? Välj den större.</p>
          </div>
          <button class="btn finder__apply" type="button" data-press data-apply>Välj ${esc(F.size)} för alla mina linnen</button>
          <div class="finder__trust">${($('template[data-trust-line]', root) || {}).innerHTML || ''}</div>
          <button class="finder__restart" type="button" data-restart>Ändra mina svar</button>
          <div class="finder__chart-title">Hela storlekstabellen</div>
          <div class="finder__chart">${SZ.map((z, i) => `<div class="finder__cell${i === F.idx ? ' is-on' : ''}"><strong>${esc(z)}</strong><span><span>Passar bh-storlek:</span> ${fits(i, s.sys)}</span><span><span>Bystmått:</span> ${bust(i, s.unit)}</span></div>`).join('')}</div>`;
      }
    }
    const isOpen = () => !!root && !root.hidden;
    let prevFocus = null;
    function open() {
      if (!root) return;
      s.step = 0; render();
      prevFocus = document.activeElement;
      root.hidden = false; root.classList.remove('is-closing'); lock(true);
      sheet.style.transform = '';
      setTimeout(() => { const b = $('button', body); b && b.focus({ preventScroll: true }); }, 50);
    }
    function close() {
      if (!isOpen() || root.classList.contains('is-closing')) return;
      root.classList.add('is-closing');
      setTimeout(() => { root.hidden = true; root.classList.remove('is-closing'); sheet.style.transform = ''; if (!$('[data-drawer].is-open')) lock(false); prevFocus && prevFocus.focus && prevFocus.focus({ preventScroll: true }); }, 200);
    }
    if (root) {
      root.addEventListener('click', e => {
        const t = e.target;
        if (t.closest('[data-finder-close]')) { close(); return; }
        const segB = t.closest('[data-f]');
        if (segB) { if (segB.dataset.f === 'System') { s.sys = segB.dataset.v; s.band = null; s.cup = null; } else s.unit = segB.dataset.v; render(); return; }
        if (t.closest('[data-band]')) { s.band = Number(t.closest('[data-band]').dataset.band); render(); return; }
        if (t.closest('[data-cup]')) { s.cup = Number(t.closest('[data-cup]').dataset.cup); render(); return; }
        if (t.closest('[data-know]')) { s.know = !s.know; render(); return; }
        if (t.closest('[data-find]')) { if (calc().ok) { s.step = 1; render(); sheet.scrollTop = 0; } return; }
        if (t.closest('[data-restart]')) { s.step = 0; render(); return; }
        // Apply: fill every tank's size, close, then take the shopper back up to the buy box (e.g. from the FAQ link).
        if (t.closest('[data-apply]')) { emit('alma:size', { size: calc().size }); close(); setTimeout(() => { const bb = [...buyboxes][0]; bb && bb.frame(); }, 260); }
      });
      root.addEventListener('input', e => {
        if (e.target.matches('[data-meas]')) {
          s.meas = e.target.value;
          const ok = calc().ok, b = $('[data-find]', body); if (b) b.disabled = !ok;
        }
      });
      // Mobile bottom sheet: drag the handle down to dismiss.
      const handle = $('[data-finder-handle]', root); let drag = null;
      handle.addEventListener('pointerdown', e => { drag = { y: e.clientY, dy: 0 }; try { handle.setPointerCapture(e.pointerId); } catch (_) {} });
      handle.addEventListener('pointermove', e => { if (!drag) return; drag.dy = Math.max(0, e.clientY - drag.y); sheet.style.transform = `translateY(${drag.dy}px)`; });
      const end = () => { if (!drag) return; const dy = drag.dy; drag = null; if (dy > 80) close(); else sheet.style.transform = ''; };
      handle.addEventListener('pointerup', end); handle.addEventListener('pointercancel', end);
    }
    return { open, close, isOpen };
  })();

  /* ───── Contact page (sections/main-contact.liquid) ───── */
  function initContact(root) {
    $$('[data-contact]', root).forEach(sec => {
      const done = $('[data-contact-done]', sec);
      if (done) {
        // Shopify redirects after posting, so the name and email come from sessionStorage, saved on submit.
        try {
          const d = JSON.parse(sessionStorage.getItem('alma-contact') || 'null');
          if (d) {
            const first = String(d.name || '').trim().split(/\s+/)[0];
            if (first) $('[data-done-title]', done).textContent = `Tack, ${first}! Vi har fått ditt meddelande.`;
            if (d.email) $('[data-done-text]', done).textContent = `Vi svarar till ${d.email} inom 24 timmar på vardagar. Titta gärna i skräpposten också, för säkerhets skull.`;
            sessionStorage.removeItem('alma-contact');
          }
        } catch (e) {}
        setTimeout(() => scrollToEl(done), 200);
        return;
      }
      const form = $('form', sec); if (!form) return;
      const radios = $$('input[name="contact[Topic]"]', form);
      const err = (k, msg) => { const el = $(`[data-err="${k}"]`, form); if (!el) return; el.textContent = msg || ''; el.hidden = !msg; const f = $(`[data-field="${k}"]`, form); if (f) f.classList.toggle('is-error', !!msg); };
      const showFor = () => {
        const r = radios.find(x => x.checked);
        [['order', r && r.hasAttribute('data-order')], ['sizes', r && r.hasAttribute('data-sizes')]].forEach(([k, on]) => {
          $$(`[data-show="${k}"]`, form).forEach(el => { el.hidden = !on; $$('input', el).forEach(i => { i.disabled = !on; }); });
        });
      };
      radios.forEach(r => r.addEventListener('change', () => { err('topic'); showFor(); }));
      form.addEventListener('input', e => { const k = e.target.dataset && e.target.dataset.field; if (k) err(k); });
      form.addEventListener('ddchange', e => {
        const dd = e.target.closest('.dd'), input = dd.parentElement.querySelector('[data-size-input]');
        if (input) input.value = e.detail.value;
        $('[data-dd-label]', dd).textContent = e.detail.value;
        $('[data-dd-btn]', dd).classList.remove('is-empty');
      });
      sec.addEventListener('click', e => {
        const pick = e.target.closest('[data-pick-topic]'); if (!pick) return;
        e.preventDefault();
        const r = radios.find(x => x.value === pick.dataset.pickTopic);
        if (r) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); }
        scrollToEl(form.closest('.contact__form-wrap'));
      });
      // Capture phase on document, so this runs before Shopify's hCaptcha submit handler: an incomplete
      // form stops here (no captcha challenge, no unvalidated post).
      document.addEventListener('submit', e => {
        if (e.target !== form) return;
        const v = k => (form.elements[k] ? form.elements[k].value.trim() : '');
        const problems = {
          topic: radios.length && !radios.some(r => r.checked) ? 'Välj vad ditt meddelande gäller.' : '',
          name: !v('contact[name]') ? 'Skriv ditt namn så att vi vet vem vi ska svara.' : '',
          email: !v('contact[email]') ? 'Skriv din e-postadress så att vi kan svara dig.'
            : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v('contact[email]')) ? 'E-postadressen ser inte rätt ut. Kontrollera den.' : '',
          msg: !v('contact[body]') ? 'Berätta lite om vad det gäller.' : ''
        };
        Object.entries(problems).forEach(([k, m]) => err(k, m));
        const first = Object.keys(problems).find(k => problems[k]);
        if (first) {
          e.preventDefault(); e.stopImmediatePropagation();
          const el = $(`[data-field="${first}"]`, form) || radios[0];
          scrollToEl(el.closest('.cform__field'), 24); el.focus({ preventScroll: true });
          return;
        }
        try { sessionStorage.setItem('alma-contact', JSON.stringify({ name: v('contact[name]'), email: v('contact[email]') })); } catch (ex) {}
      }, true);
      showFor();
    });
  }

  /* ───── Policy pages: "Back to top" after each section on phones (shown via CSS < 720px) ───── */
  function initPolicy(root) {
    const body = $('.shopify-policy__body', root); if (!body || body.dataset.tops) return;
    body.dataset.tops = '1';
    // Empty table rows the policy editor leaves behind (cells holding only <br>) would show as blank cards.
    $$('tr', body).forEach(tr => { if (!tr.textContent.trim()) tr.hidden = true; });
    const top = () => { const a = document.createElement('a'); a.href = '#main'; a.className = 'policy__top'; a.textContent = 'Till toppen ↑';
      a.addEventListener('click', e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }); return a; };
    $$('h2', body).slice(1).forEach(h => h.parentNode.insertBefore(top(), h));
    if ($$('h2', body).length > 1) body.appendChild(top());
  }

  /* ───── Boot ───── */
  // "Ships today" until 23:00 Dutch time (CET/CEST), "Ships tomorrow" from 23:00 to midnight.
  function initDispatch(root) {
    $$('[data-dispatch]', root).forEach(el => {
      if (el.dataset.today) return; el.dataset.today = el.textContent;
      const hourFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Amsterdam', hour: '2-digit', hourCycle: 'h23' });
      const tick = () => { el.textContent = Number(hourFmt.format(new Date())) === 23 && el.dataset.late ? el.dataset.late : el.dataset.today; };
      tick(); setInterval(tick, 60000);
    });
  }
  function initShipFee(root) { $$('[data-ship-fee]', root).forEach(el => { el.textContent = money(SHIP_FEE); }); }
  function init(root = document) {
    initShipFee(root); initDispatch(root);
    initHeader(); initCountdown(root); initTitle(root); initGallery(root); initBuybox(root); initContact(root); initPolicy(root);
  }
  init();
  cart.refresh();
  // Arriving via a menu link from another page: /#size-guide opens the finder, /#faq and /#guarantee scroll.
  if (location.hash === '#size-guide') setTimeout(() => finder.open(), 300);
  else if (location.hash.length > 1 && document.getElementById(location.hash.slice(1))) setTimeout(() => scrollToEl(document.getElementById(location.hash.slice(1))), 300);
  // Arriving from /cart (e.g. checkout's cart link): the cart page redirects here with ?cart=1.
  if (new URLSearchParams(location.search).has('cart')) {
    setTimeout(() => openDrawer('cart'), 150);
    const u = new URL(location.href); u.searchParams.delete('cart'); history.replaceState(null, '', u.pathname + u.search + u.hash);
  }
  // Arriving from a "Make The Switch" button on another page (?bundle=2): select that bundle and frame it.
  const wantBundle = Number(new URLSearchParams(location.search).get('bundle'));
  if (wantBundle > 0 && buyboxes.size) setTimeout(() => { const bb = [...buyboxes][0]; bb.select(wantBundle - 1); bb.frame(); }, 300);
  // Theme editor: re-init a section after it re-renders.
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
